import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { t } from "i18next";
import { useState } from "react";
import { CURRENCY, DEFAULT_VAT_PERCENTAGE } from "../../constants/Variables";
import {
  calculateLossAmount,
  calculateVat,
  calculateProductFinalPrice,
} from "../../utils";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
  "& .MuiPaper-root": {
    borderRadius: "12px",
    maxWidth: "600px",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      margin: 16,
    },
  },
}));

const Preview = ({ open, onClose, inputValues, settings }) => {
  const { price = 0, profitMargin = 0, discount = 0 } = inputValues || {};

  const [deliveryType, setDeliveryType] = useState("standard");
  const vat = settings?.tax || DEFAULT_VAT_PERCENTAGE;
  const standardShippingFee = settings?.shippingFee || 0;
  const expressShippingFee = settings?.expressShippingFee || 0;
  const platformFeeEcom = settings?.platformFeeEcom || 0;

  const shippingFee =
    deliveryType === "standard" ? standardShippingFee : expressShippingFee;

  // Calculate final price
  const finalPrice = calculateProductFinalPrice(price, discount, profitMargin);

  // Calculate VAT amount
  const vatAmount = calculateVat(finalPrice, vat);

  // Calculate loss amount
  const lossAmount = calculateLossAmount(price, discount, profitMargin);

  // Calculate total with VAT
  const totalWithVat = finalPrice + vatAmount;

  // Calculate total with all charges
  const totalWithCharges = totalWithVat + shippingFee + platformFeeEcom;

  const priceRow = (label, value, isBold = false, color = "inherit") => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      py={1.5}
      borderBottom="1px solid #f0f0f0"
    >
      <Typography variant="body1" fontWeight={isBold ? 600 : 400} color={color}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={isBold ? 600 : 400} color={color}>
        {value}
      </Typography>
    </Stack>
  );

  return (
    <BootstrapDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight={600}>
            {t("pricingBreakdown")}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            animation: "fadeIn 0.3s ease-in",
            "@keyframes fadeIn": {
              from: {
                opacity: 0,
                transform: "translateY(-10px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <Box bgColor="#fafafa" borderRadius="8px" p={2} mb={2}>
            {priceRow(
              t("purchasePriceWithoutVat"),
              `${price} ${CURRENCY}`,
              false
            )}
            {priceRow(t("profitMargin"), `${profitMargin}%`, false)}
            {priceRow(t("discount"), `${discount} ${CURRENCY}`, false)}
            {priceRow(
              t("finalPrice"),
              `${finalPrice} ${CURRENCY}`,
              true,
              "primary.main"
            )}
            {lossAmount < 0 && (
              <Typography
                mt={2}
                variant="body2"
                fontWeight={400}
                color="warning.main"
              >
                {t("warning")}:{" "}
                {t("youAreLosing", { amount: lossAmount, currency: CURRENCY })}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Tax and Charges */}
          <Typography variant="h6" fontWeight={600} mb={2}>
            {t("taxAndCharges")}
          </Typography>

          <Box>
            <FormControl>
              <Box>
                <RadioGroup
                  value={deliveryType}
                  onChange={(e) => setDeliveryType(e.target.value)}
                >
                  <Box display={"flex"}>
                    <FormControlLabel
                      value="standard"
                      control={<Radio />}
                      label={t("standardDelievery")}
                    />
                    <FormControlLabel
                      value="express"
                      control={<Radio />}
                      label={t("expressDelievery")}
                    />
                  </Box>
                </RadioGroup>
              </Box>
            </FormControl>
          </Box>

          <Box bgColor="#fafafa" borderRadius="8px" p={2} mb={2}>
            {priceRow(
              `${t("vatAmount", { vat: vat })}`,
              `${vatAmount} ${CURRENCY}`,
              false
            )}
            {priceRow(t("shippingFee"), `${shippingFee} ${CURRENCY}`, false)}
            {priceRow(
              t("platformCharges"),
              `${platformFeeEcom} ${CURRENCY}`,
              false
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Total */}
          <Box bgColor="#fafafa" borderRadius="8px" p={2} mb={2}>
            {priceRow(
              t("totalAmount"),
              `${totalWithCharges} ${CURRENCY}`,
              true,
              "primary.main"
            )}
          </Box>
        </Box>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default Preview;
